// Ajouter une journée à la main : quand personne n'a écrit sur WhatsApp, ou
// pour corriger après coup.
//
// Les heures se tapent comme on les dit — « 7h », « 7h30 », « 15.30 ». Ce qui
// a été compris s'affiche sous le champ AVANT d'enregistrer : une heure mal
// lue doit se voir tout de suite, pas au moment de la paie.

import { messageErreur } from '@imp/shared/message-erreur';
import { formatMinutes, parseHeureJour, parsePauseMinutes } from '@imp/shared/releve-heures';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { creerReleve, type EmployeHeures } from '@/modules/rh/api';

export function SaisieReleveForm({
  employes,
  mois,
  onEnregistre,
}: {
  employes: readonly EmployeHeures[];
  mois: string;
  onEnregistre: () => void;
}) {
  const [jour, setJour] = useState(() => `${mois}-01`);
  const [employeId, setEmployeId] = useState('');
  const [nomLibre, setNomLibre] = useState('');
  const [equipe, setEquipe] = useState('');
  const [chantier, setChantier] = useState('');
  const [debutTexte, setDebutTexte] = useState('');
  const [finTexte, setFinTexte] = useState('');
  const [pauseTexte, setPauseTexte] = useState('');
  const [note, setNote] = useState('');

  const debut = parseHeureJour(debutTexte);
  const fin = parseHeureJour(finTexte);
  const pause = pauseTexte.trim() === '' ? null : parsePauseMinutes(pauseTexte);
  const employe = employes.find((e) => e.id === employeId);
  const nom = employe?.prenom ?? nomLibre.trim();

  const brut = debut !== null && fin !== null && fin > debut ? fin - debut : null;

  const erreur =
    nom === ''
      ? 'Indiquez qui a travaillé.'
      : debutTexte.trim() !== '' && debut === null
        ? 'Heure de début illisible.'
        : finTexte.trim() !== '' && fin === null
          ? 'Heure de fin illisible.'
          : pauseTexte.trim() !== '' && pause === null
            ? 'Durée de pause illisible.'
            : debut === null || fin === null
              ? 'Indiquez l’heure de début et l’heure de fin.'
              : fin <= debut
                ? 'L’heure de fin doit être après l’heure de début.'
                : null;

  const enregistrer = useMutation({
    mutationFn: async () => {
      if (debut === null || fin === null) throw new Error('Horaire incomplet.');
      await creerReleve({
        jour,
        employeId: employeId === '' ? null : employeId,
        nom,
        equipe: equipe.trim() === '' ? null : equipe.trim(),
        chantierId: null,
        chantierLibelle: chantier.trim() === '' ? null : chantier.trim(),
        debut,
        fin,
        pauseDeclaree: pause,
        note: note.trim() === '' ? null : note.trim(),
        source: 'saisie',
        sourceTexte: null,
      });
    },
    onSuccess: () => {
      setDebutTexte('');
      setFinTexte('');
      setPauseTexte('');
      setNote('');
      onEnregistre();
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ajouter une journée</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="rel_jour">Jour</Label>
            <Input
              id="rel_jour"
              type="date"
              value={jour}
              onChange={(e) => {
                setJour(e.target.value);
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rel_salarie">Salarié</Label>
            <Select
              id="rel_salarie"
              value={employeId}
              onChange={(e) => {
                setEmployeId(e.target.value);
              }}
            >
              <option value="">— nom libre —</option>
              {employes.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.prenom} {e.nom}
                </option>
              ))}
            </Select>
          </div>
          {employeId === '' ? (
            <div className="space-y-1">
              <Label htmlFor="rel_nom">Nom</Label>
              <Input
                id="rel_nom"
                value={nomLibre}
                placeholder="Prénom"
                onChange={(e) => {
                  setNomLibre(e.target.value);
                }}
              />
            </div>
          ) : null}
          <div className="space-y-1">
            <Label htmlFor="rel_equipe">Équipe</Label>
            <Input
              id="rel_equipe"
              value={equipe}
              placeholder="Jo, Julien…"
              onChange={(e) => {
                setEquipe(e.target.value);
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rel_chantier">Chantier</Label>
            <Input
              id="rel_chantier"
              value={chantier}
              placeholder="Pierrerue, Saint-Paul…"
              onChange={(e) => {
                setChantier(e.target.value);
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rel_debut">Début</Label>
            <Input
              id="rel_debut"
              value={debutTexte}
              placeholder="7h"
              inputMode="numeric"
              onChange={(e) => {
                setDebutTexte(e.target.value);
              }}
            />
            {debutTexte.trim() !== '' ? (
              <p
                className={`text-xs ${debut === null ? 'text-destructive' : 'text-muted-foreground'}`}
              >
                {debut === null ? 'Illisible' : `Compris : ${formatMinutes(debut)}`}
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            <Label htmlFor="rel_fin">Fin</Label>
            <Input
              id="rel_fin"
              value={finTexte}
              placeholder="16h30"
              inputMode="numeric"
              onChange={(e) => {
                setFinTexte(e.target.value);
              }}
            />
            {finTexte.trim() !== '' ? (
              <p
                className={`text-xs ${fin === null ? 'text-destructive' : 'text-muted-foreground'}`}
              >
                {fin === null ? 'Illisible' : `Compris : ${formatMinutes(fin)}`}
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            <Label htmlFor="rel_pause">Pause déclarée</Label>
            <Input
              id="rel_pause"
              value={pauseTexte}
              placeholder="30 (minutes) — vide si rien n’a été dit"
              onChange={(e) => {
                setPauseTexte(e.target.value);
              }}
            />
            {pauseTexte.trim() !== '' ? (
              <p
                className={`text-xs ${pause === null ? 'text-destructive' : 'text-muted-foreground'}`}
              >
                {pause === null ? 'Illisible' : `Compris : ${formatMinutes(pause)}`}
              </p>
            ) : null}
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="rel_note">Note</Label>
            <Input
              id="rel_note"
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => {
              enregistrer.mutate();
            }}
            disabled={erreur !== null || enregistrer.isPending}
          >
            {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
          {brut !== null ? (
            <span className="text-muted-foreground text-sm">
              Journée de {formatMinutes(brut)} avant déduction de la pause.
            </span>
          ) : null}
          {erreur !== null ? <span className="text-destructive text-sm">{erreur}</span> : null}
          {enregistrer.isError ? (
            <span className="text-destructive text-sm">{messageErreur(enregistrer.error)}</span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
